/**
 * Adapter: converte o payload do endpoint ``api/v3/snapshot/`` para o
 * shape que a view ``InputsV3.js`` (cópia literal do V2) espera.
 *
 * Esta é a primeira troca do "snapshot-first": a **leitura** passa a
 * vir do snapshot V3 + runtime mock, mas a estrutura da view continua
 * sendo a do V2 (tabs, drawer, cards, 3-views). Ações (upload, reset,
 * etc.) **ainda batem nos endpoints V2** — isso é intencional, será
 * substituído ponto-a-ponto nas próximas iterações.
 *
 * Mapeamentos principais:
 *   - InputV3.meta_name              → input_id e input_control_id
 *   - InputV3.file_name              → input_name.{pt,en,es} (mesmo texto)
 *   - InputV3.description            → input_details (HTML simples)
 *   - InputV3.origin                 → source (file_upload / auto_generated)
 *   - runtime.inputs[m].attention_label → stages.{data_source, data_structure, cross_linking}
 *   - runtime.inputs[m].responsible_user → responsible: [{id, name}]
 */

import { classifyInputAggregate } from "./inputsV3StateRules";

const ORIGIN_TO_SOURCE = {
  upload: "file_upload",
  auto_generated: "auto_generated",
  ui_managed: "auto_generated",
  ui_managed_system_editable: "auto_generated",
};

/**
 * Resolve o(s) tipo(s) de arquivo aceito(s) por um input.
 *
 * Ordem de precedencia:
 *  1. ``input.expected_file_types`` declarado no descriptor (lista) —
 *     fonte de verdade. Imperativos sempre tem; declarativos podem
 *     ter (recomendado) ou nao (lista vazia).
 *  2. Fallback para parsers declarativos legados (snapshots antigos
 *     sem o campo): deriva do primeiro ``parse_steps[0].kind``.
 *  3. Default ultimo recurso: ``["xlsx"]``.
 *
 * Retorna sempre uma lista (pode ter 0, 1 ou N itens). Lista vazia =
 * UI nao deve restringir o file picker (parser aceita qualquer
 * formato e tenta auto-deteccao).
 */
const TYPES_FROM_INPUT = (input) => {
  if (Array.isArray(input.expected_file_types)) {
    return input.expected_file_types;
  }
  const first = (input.parse_steps || [])[0];
  if (!first) return ["xlsx"];
  if (first.kind === "zipped_csv") return ["zip"];
  if (first.kind === "csv") return ["csv"];
  if (first.kind === "parquet") return ["parquet"];
  return ["xlsx"];
};

/**
 * Fase do ciclo de vida do input (consumido pelo donut/classifier).
 *
 * Valores:
 *   - ``pending``    — periodo aberto, nada foi enviado ainda.
 *   - ``not_sent``   — periodo passou sem envio.
 *   - ``queued``     — arquivo recebido, aguardando worker pegar.
 *   - ``processing`` — worker rodando o parse / structure check.
 *   - ``ok``         — pipeline terminou com sucesso (sem warnings).
 *   - ``warning``    — pipeline terminou com warnings (severity 2).
 *   - ``pending_decision`` — arquivo chegou mas ha pergunta em aberto
 *     bloqueando o avanco do pipeline (D-22, InputDecisionV3).
 *   - ``blocked``    — pipeline bloqueou (severity 3, error de parse).
 *   - ``system_error`` — falha de infraestrutura.
 *   - ``permanent``  — input semi-fixo/fixo, sempre disponivel.
 *
 * O estado transitorio ``uploading`` (HTTP request em curso, do lado
 * do cliente) NAO e derivado aqui — e imposto pelo componente pai via
 * prop ``isUploading`` sempre que necessario.
 */
export function derivePhase({ attentionLabel, controlState, isPermanent }) {
  // Critical SEMPRE vence permanent. Defesa contra backend inconsistente
  // que envie ``{is_permanent_for_month=true, attention_label='critical'}``
  // simultaneamente — caso classico: input ``semi_fixed`` com entregas
  // previas em sucesso (``has_prior_success``) que sofre rejeicao
  // explicita no mes corrente. Sem esta guarda, ``isPermanent`` curto-
  // circuitava antes do switch e o card aparecia verde permanente
  // apesar do erro hard.
  // Onda 3.4 (CCU Testes, mai/2026): ``rejected_by_xlink`` e' um
  // sub-tipo de critico onde a ESTRUTURA foi aceita pelo parser, mas
  // o input foi rejeitado por decisao D-22 ``reject_input`` no
  // cruzamento contra outro participante. O ``controlState`` continua
  // ``"error"`` (bloqueia downstream), mas a phase semantica e' outra
  // para que o ``phaseToStages`` emita ``data_structure=success`` +
  // ``cross_linking=error`` — "Arquivo rejeitado" aparece na caixa
  // CRUZAMENTO, nao em ESTRUTURA.
  if (attentionLabel === "rejected_by_xlink") {
    return "rejected_by_xlink";
  }
  if (
    attentionLabel === "critical" ||
    controlState === "error" ||
    controlState === "system_error"
  ) {
    if (controlState === "system_error") return "system_error";
    if (controlState === "error") return "blocked";
    return "not_sent";
  }
  if (isPermanent) return "permanent";
  switch (attentionLabel) {
    case "ok":
      return "ok";
    case "warning":
      return "warning";
    case "pending_decision":
      return "pending_decision";
    case "processing":
      // Backend diferencia ``uploaded`` (no queue) de ``processing``
      // (worker rodando). Colapsamos em ``queued``/``processing`` para
      // que a UI possa graduar a mensagem.
      return controlState === "processing" ? "processing" : "queued";
    case "attention":
      return "warning";
    case "pending":
    default:
      return "pending";
  }
}

/**
 * Trio ``{data_source, data_structure, cross_linking}`` (shape V2) a
 * partir do ``phase``. E um mapeamento descendente: cada fase tem uma
 * representacao honesta e unica nos 3 estagios legacy. Essencial para
 * eliminar o bug anterior em que ``critical`` mentia ``data_structure
 * = success`` (estrutura bloqueada aparecendo como OK).
 */
function phaseToStages(phase) {
  switch (phase) {
    case "permanent":
    case "ok":
      // ``cross_linking`` fica como ``waiting`` por default. Quando o
      // backend publicar ``runtime.cross_linking.state`` (input
      // participa de cross-links e ja rodaram), o adapter sobrescreve
      // este estagio em ``makeInputRow``.
      return {
        data_source: "success",
        data_structure: "success",
        cross_linking: "waiting",
      };
    case "warning":
      // Warning de PARSE — atribuimos ao ``data_structure``. Se ALEM
      // disso houver warning/error de cross-link, ``makeInputRow``
      // pinta a 3a barra com base no ``runtime.cross_linking.state``.
      return {
        data_source: "success",
        data_structure: "warning",
        cross_linking: "waiting",
      };
    case "pending_decision":
      // Arquivo aceito estruturalmente, mas o avanco esta suspenso
      // aguardando resposta humana. Marcamos ``cross_linking`` como
      // ``pending_decision`` (estagio onde a pergunta aparece com
      // mais frequencia — decisoes de cross-link dominam em D-22);
      // estrutura fica ``success`` porque o parse deu ok.
      return {
        data_source: "success",
        data_structure: "success",
        cross_linking: "pending_decision",
      };
    case "queued":
      return {
        data_source: "success",
        data_structure: "waiting",
        cross_linking: "waiting",
      };
    case "processing":
      return {
        data_source: "success",
        // Onda 3.1 (mai/2026): stage interno passa a usar
        // ``"processing"`` (canonico, alinhado ao backend) em vez de
        // ``"running"``. Componentes V3 aceitam ambos via
        // ``isProcessingStage`` enquanto a transicao roda.
        data_structure: "processing",
        cross_linking: "waiting",
      };
    case "blocked":
      return {
        data_source: "success",
        data_structure: "error",
        cross_linking: "waiting",
      };
    case "rejected_by_xlink":
      // Onda 3.4: estrutura foi aceita pelo parser (parse=success),
      // mas a decisao D-22 de cruzamento rejeitou o arquivo. "Arquivo
      // rejeitado" deve aparecer na caixa CRUZAMENTO. O ``makeInputRow``
      // ainda pode sobrescrever ``cross_linking`` com o agregado do
      // runtime (``runtime.cross_linking.state``) — neste caso o
      // agregador ja vai retornar ``error`` para o input recusado, o
      // que reforça a coloracao da caixa cruzamento.
      return {
        data_source: "success",
        data_structure: "success",
        cross_linking: "error",
      };
    case "system_error":
      return {
        data_source: "success",
        data_structure: "system_error",
        cross_linking: "waiting",
      };
    case "not_sent":
    case "pending":
    default:
      // Sem upload (atrasado ou periodo aberto): todos os estagios
      // ficam cinza. A informacao nao existe — nao ha o que colorir
      // de forma diferente. Se no futuro quisermos destacar "atraso",
      // deve ser um badge na UI, nao uma cor no donut.
      return {
        data_source: "waiting",
        data_structure: "waiting",
        cross_linking: "waiting",
      };
  }
}

/**
 * Aplica um bloco ``runtime.inputs[meta]`` canônico sobre uma linha já
 * montada para a UI. Usado quando um endpoint incremental (ex. drawer)
 * observa um estado mais novo que o snapshot carregado pela tela principal.
 */
export function applyRuntimeToInputRow(row, runtimeInput) {
  if (!row || !runtimeInput) return row;

  // PR 2 refactor temporal: ``covered_by`` (novo) carrega a janela
  // do control resolvido — fonte canonica para "permanente neste mes",
  // "herdado de FEV/2026", "concluido sem arquivo" etc.
  //
  // Onda 1.5: o backend continua emitindo ``is_permanent_for_month``
  // cru no runtime mesmo no payload novo (``covered_by`` foi adicionado
  // em paralelo, nao em substituicao). Antes, este bloco tinha um
  // ternario redundante (``coveredBy != null ? X : X``) que era um
  // TODO nao terminado da migracao do PR 2 — o ramo "novo" deveria
  // ler de ``covered_by`` mas terminou usando o mesmo fallback.
  // Simplificamos para uma unica leitura; quando o backend formalizar
  // ``covered_by.is_permanent``, alteramos aqui em UM lugar.
  const coveredBy = runtimeInput.covered_by || null;
  const isPermanent = !!runtimeInput.is_permanent_for_month;
  const phase = derivePhase({
    attentionLabel: runtimeInput.attention_label,
    controlState: runtimeInput.control_state || null,
    isPermanent,
  });
  const stages = phaseToStages(phase);
  const xl = runtimeInput.cross_linking || null;
  stages.cross_linking = crossLinkStageFromRuntime(xl, stages);

  const uploadedByRaw = runtimeInput.state_params?.uploaded_by || null;
  const uploadedBy = uploadedByRaw
    ? {
        name: uploadedByRaw.name || null,
        email: uploadedByRaw.email || null,
        is_current_user: !!uploadedByRaw.is_current_user,
      }
    : null;
  const respList =
    runtimeInput.responsible_users && runtimeInput.responsible_users.length
      ? runtimeInput.responsible_users
      : runtimeInput.responsible_user
        ? [runtimeInput.responsible_user]
        : [];

  row.stages = stages;
  row.phase = phase;
  row.control_state = runtimeInput.control_state || null;
  row.responsible = respList
    .filter((r) => r?.name)
    .map((r) => ({
      id: r.id != null ? r.id : r.email || r.name,
      name: r.name,
      is_current_user: !!r.is_current_user,
    }));
  row.uploaded_by = uploadedBy;
  row.updated_by_username = uploadedBy?.name || null;
  row.updated_in = runtimeInput.state_params?.delivered_at
    ? formatIsoAsLocalYmd(runtimeInput.state_params.delivered_at)
    : null;
  row.instructions_md = runtimeInput.instructions_md || null;
  row.instructions_updated_at = runtimeInput.instructions_updated_at || null;
  row.instructions_updated_by = runtimeInput.instructions_updated_by || null;
  row.is_mine = respList.some((r) => r?.is_current_user);
  row.is_permanent_for_month = isPermanent;
  row.is_required = !isPermanent;
  row.attention_label = runtimeInput.attention_label || "pending";
  row.has_prior_success = !!runtimeInput.has_prior_success;
  row.observation_count = Number(
    runtimeInput.state_params?.observation_count || 0,
  );
  row.cross_linking = xl;
  // ``xlinks_in_flight`` (D-MTX-1): numero de xlinks deste input que
  // ainda estao em pre-execucao (sem CSL, ou CSL em pending/blocked/
  // running/stale). Quando >0, o InputBoxV3 sobrepoe um overlay de
  // listras animadas a cor base — pip mantem o veredito agregado e
  // ganha sinal visual de "tem coisa rodando agora".
  row.xlinks_in_flight = Number(xl?.xlinks_in_flight || 0);
  // ``is_waived``: pos-refactor PR 2, prioriza ``covered_by.is_waived``
  // (novo, vem do state == 'waived' OU waived_at != NULL do control
  // resolvido). Fallback no payload antigo (``runtimeInput.is_waived``)
  // ate o backend velho sair.
  row.is_waived = !!(coveredBy?.is_waived ?? runtimeInput.is_waived);
  row.waived_at = runtimeInput.state_params?.waived_at || null;
  row.waived_by = runtimeInput.state_params?.waived_by || null;
  // ``covered_by``: janela do control resolvido. Frontend usa para
  // exibir "Entregue em FEV/2026 (cobertura jan..dez)" no DrawerHeader,
  // periodicityLabel no NowPane, badge "Coberto pelo FY" no InputBox.
  row.covered_by = coveredBy;
  row.quality_score =
    xl?.worst_fact_coverage?.ratio != null
      ? Math.max(
          0,
          Math.min(100, Math.round(100 * (1 - xl.worst_fact_coverage.ratio))),
        )
      : null;
  return row;
}

// ---------------------------------------------------------------------------
// Mapeamento ``CrossLinkControlV3.state`` -> substate do tracinho.
// Mesma tabela usada inline em ``makeInputRow`` quando o input
// participa de cross-link. Centralizada aqui para reuso pelo
// ``classifyInputFromRuntimeState`` (consumido pelo OverviewV3).
// ---------------------------------------------------------------------------
const CSL_STATE_TO_STAGE = {
  success: "success",
  partial: "partial",
  partial_warning: "partial_warning",
  warning: "warning",
  error: "error",
  system_error: "system_error",
  pending_decision: "pending_decision",
  // Onda 3.1: backend agora envia ``processing`` (unificado). Mantemos
  // ``running`` no mapping como compat defensivo (snapshots cacheados,
  // migracao 0029 nao rodada em algum ambiente, etc.) — ambos sao
  // colapsados para o mesmo stage interno ``"processing"``.
  processing: "processing",
  running: "processing",
  pending: "waiting",
  blocked: "waiting",
  stale: "waiting",
};

function isDataStructureSettled(stages) {
  return (
    stages.data_structure === "success" ||
    stages.data_structure === "warning" ||
    stages.data_structure === "error" ||
    stages.data_structure === "system_error"
  );
}

function crossLinkStageFromRuntime(xl, stages) {
  if (!isDataStructureSettled(stages)) return "waiting";
  if (!xl) {
    return stages.data_structure === "success" ||
      stages.data_structure === "warning"
      ? "success"
      : "waiting";
  }
  // Auditoria mai/2026 (POC CCU Testes): ``pending_decision`` precisa
  // ser detectado ANTES do branch de ``max_severity>=3`` — o backend
  // emite ``max_severity=3`` no mesmo run em que o cross-link cai na
  // banda critica COM caminho de destrava humano (D-22). Sem este
  // early-return o adapter retornava ``"error"`` e o pip/donut ficava
  // vermelho liso em vez do laranja hachurado de ``pending_decision``,
  // mascarando o caminho de acao do consultor.
  if (xl.state === "pending_decision") return "pending_decision";
  const maxSeverity = Number(xl.max_severity || 0);
  if (maxSeverity >= 3) return "error";
  if (maxSeverity === 2) {
    return Number(xl.xlinks_in_flight || 0) > 0 ||
      xl.state === "partial" ||
      xl.state === "partial_warning"
      ? "partial_warning"
      : "warning";
  }
  if (xl.state) return CSL_STATE_TO_STAGE[xl.state] || "waiting";
  // Participa de cross-link, mas nenhum control/run valido existe ainda:
  // isso nao e "verificado"; fica aguardando em vez de virar verde.
  if (Number(xl.count || 0) > 0) return "waiting";
  return stages.data_structure === "success" ||
    stages.data_structure === "warning"
    ? "success"
    : "waiting";
}

/**
 * Classifica um par (input, periodo) para o bucket exibido pelo
 * InputBox/OverviewV3 a partir do shape "achatado" devolvido pelo
 * ``progress_overview`` (ver ``inputs_state_by_period``):
 *
 *   { attention_label, control_state, is_permanent_for_month,
 *     cross_linking_state }
 *
 * Reaproveita ``derivePhase`` + ``phaseToStages`` (mesma logica que o
 * snapshot adapter usa para o mes selecionado) e aplica o refinamento
 * de cross-linking que vive em ``makeInputRow``. Em seguida delega
 * para ``classifyInputAggregate`` (paleta canonica do InputBox) — o
 * objetivo dessa centralizacao e' garantir que a cor que o
 * OverviewV3 pinta para um par (input, mes) seja IDENTICA a cor que
 * o InputBoxV3 pintaria se o usuario navegasse para aquele mes.
 *
 * Retorna uma das chaves de ``INPUT_STATE_COLORS`` (rules):
 *   ``critical | pending_decision | attention | delivered_ok |
 *     in_progress | not_sent``
 */
export function classifyInputFromRuntimeState(rt) {
  const safe = rt || {};
  const phase = derivePhase({
    attentionLabel: safe.attention_label,
    controlState: safe.control_state,
    isPermanent: !!safe.is_permanent_for_month,
  });
  const stages = phaseToStages(phase);
  // Refinamento da 3a barra (cross_linking) — espelha makeInputRow.
  // O ``data_structure`` precisa ter chegado a um veredito antes de
  // exibir o estado de xlink (caso contrario fica ``waiting``).
  stages.cross_linking = crossLinkStageFromRuntime(
    {
      state: safe.cross_linking_state,
      count: safe.cross_linking_count,
      max_severity: safe.cross_linking_max_severity,
      xlinks_in_flight: safe.xlinks_in_flight,
    },
    stages,
  );
  return classifyInputAggregate({
    stages,
    is_permanent_for_month: !!safe.is_permanent_for_month,
    phase,
  });
}

/**
 * Regra V3 simplificada do status do mes. Separa explicitamente dois
 * dominios que o V2 confundia no mesmo badge vermelho:
 *
 * 1. ``waiting`` (cinza): o mes ainda nao esta pronto por qualquer
 *    razao — inputs faltando, arquivos rejeitados pelo cliente,
 *    decisoes humanas em aberto, parse rodando, etc. A "razao exata"
 *    do atraso aparece nos proprios inputs; o badge agrega tudo em
 *    "esta incompleto".
 * 2. ``error`` (vermelho): erro de **runtime do sistema** — e so
 *    quando ``phase === "system_error"`` em algum input (a V3 ja
 *    distingue isso do erro do cliente). Esse e o unico caso que
 *    aciona o time de Integration.
 * 3. ``complete`` (verde): todos os inputs OK (success/warning) e
 *    nenhuma decisao pendente.
 * 4. ``stale`` (verde-amarelo): reservado para o futuro. Significa
 *    "ja calculei antes, mas alguem re-subiu input depois e o
 *    pipeline ainda nao reprocessou". Requer rastreamento do
 *    CALCULATION pipeline (proximas frentes); por enquanto
 *    retornamos ``complete`` quando tudo esta OK.
 *
 * Arquivos rejeitados pelo cliente (``phase === "blocked"``) caem em
 * ``waiting``, nao em ``error`` — o sistema esta saudavel, so esta
 * esperando o cliente corrigir o arquivo.
 */
function deriveMonthStatus(inputsStatus) {
  const flat = [];
  for (const g of inputsStatus) {
    for (const inp of g.inputs || []) flat.push(inp);
  }
  if (flat.length === 0) return "waiting";

  // Runtime / infra error → badge vermelho com tooltip de "acionar
  // administrador". Diferencia de ``blocked`` (erro do cliente).
  const hasSystemError = flat.some(
    (i) =>
      i.phase === "system_error" || i.stages?.data_structure === "system_error",
  );
  if (hasSystemError) return "error";

  // ``permanent`` entra como OK (arquivo semi-fixo valido de periodo
  // anterior); ``ok`` e ``warning`` tambem contam como "entregue".
  // Qualquer outra coisa (blocked/pending_decision/pending/not_sent/
  // queued/processing) ainda impede o mes de ser "completo".
  const isDelivered = (i) =>
    i.phase === "permanent" || i.phase === "ok" || i.phase === "warning";
  const allDelivered = flat.every(isDelivered);
  if (allDelivered) {
    // TODO (Camada stale): quando o CALCULATION pipeline V3 comecar
    // a registrar ``last_calculation_at``, comparar com o
    // ``updated_at`` mais recente dos inputs; se input > calc,
    // retornar "stale".
    return "complete";
  }

  return "waiting";
}

/**
 * Extrai usuários únicos (responsáveis) do runtime — alimenta os
 * dropdowns do drawer e do filtro por responsável.
 */
// Converte um timestamp ISO (``2026-04-17T22:30:00Z``) para a data
// ``YYYY-MM-DD`` no fuso LOCAL do browser. Esse helper existe porque
// o backend armazena ``delivered_at`` em UTC, e fazer ``slice(0, 10)``
// da string ISO devolve a data UTC — em fusos negativos, o dia local
// ja pode ser diferente. A UI esperaria sempre a data que o usuario
// percebeu ao enviar o arquivo ("enviei dia 17"), nao a data do
// relogio UTC.
function formatIsoAsLocalYmd(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  } catch (_) {
    return String(iso).slice(0, 10);
  }
}

function collectUsers(runtime) {
  // Preferimos ``runtime.eligible_users`` (lista canonica de
  // membros da implementacao com User PK real, enviada pelo backend
  // apenas para DEV/IMG/CAD). E essa a lista que o seletor de
  // responsaveis precisa, e os IDs sao numericos (User PK) — que
  // o endpoint ``v3/inputs/<meta>/responsibles/`` exige.
  if (Array.isArray(runtime.eligible_users) && runtime.eligible_users.length) {
    return [...runtime.eligible_users].sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || "")),
    );
  }
  const map = new Map();
  for (const meta in runtime.inputs || {}) {
    const rt = runtime.inputs[meta];
    const list =
      rt.responsible_users && rt.responsible_users.length
        ? rt.responsible_users
        : rt.responsible_user
          ? [rt.responsible_user]
          : [];
    for (const r of list) {
      if (r && r.name) {
        const id = r.id || r.email || r.name;
        if (!map.has(id)) map.set(id, { id, name: r.name });
      }
    }
  }
  return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Constrói a lista de grupos + inputs no shape V2.
 */
function buildInputsStatus(snapshot, runtime) {
  const groupsSorted = [...(snapshot.input_groups || [])].sort(
    (a, b) => (a.position || 0) - (b.position || 0),
  );
  const byGroup = new Map();
  for (const g of groupsSorted) byGroup.set(g.meta_name, []);
  const ungrouped = [];
  for (const inp of snapshot.inputs || []) {
    const key = inp.group_meta_name;
    if (key && byGroup.has(key)) byGroup.get(key).push(inp);
    else ungrouped.push(inp);
  }
  for (const list of byGroup.values()) {
    list.sort(
      (a, b) => (a.position_in_group || 0) - (b.position_in_group || 0),
    );
  }

  const makeInputRow = (inp) => {
    const rt = runtime.inputs?.[inp.meta_name] || {};
    const isPermanent = !!rt.is_permanent_for_month;
    const phase = derivePhase({
      attentionLabel: rt.attention_label,
      controlState: rt.control_state || null,
      isPermanent,
    });
    const stages = phaseToStages(phase);
    // 3a barrinha "Integridade entre dados" reflete o estado REAL do
    // cross-linking quando o backend publica ``runtime.cross_linking``.
    // Mapeamento ``CrossLinkControlV3.state`` -> stage:
    //   success      -> success
    //   warning      -> warning
    //   error/system -> error
    //   pending      -> waiting (cross-link enfileirado mas nao rodou)
    //   running      -> running (worker ativo)
    //   blocked      -> waiting (bloqueado por dependencia)
    // Quando ``runtime.cross_linking == null`` (input nao participa
    // de cross-link), o terceiro traco representa "nao ha nada a
    // verificar". Se o parsing ja rodou sem erro, mostramos verde
    // (success) em vez de manter "waiting" indefinidamente.
    const xl = rt.cross_linking || null;
    // O cross-linking e LOGICAMENTE downstream do parse de estrutura: so
    // faz sentido cruzar dados depois que cada arquivo passou pelo seu
    // proprio check estrutural. Se ``data_structure`` ainda nao chegou
    // a um veredito (success/warning/error/system_error) — ex.: usuario
    // acabou de re-enviar o arquivo e o pipeline esta em ``running``/
    // ``waiting`` — NAO devemos refletir um ``CrossLinkControlV3.state``
    // remanescente da rodada anterior. Caso contrario, o card pisca a
    // 3a barra com cor da rodada antiga (amarela/verde) enquanto a 2a
    // ja voltou a cinza, gerando uma leitura impossivel ("xlink antes
    // do parse"). O guard abaixo trava a 3a barra em ``waiting`` ate
    // que o parse produza um resultado deterministico.
    stages.cross_linking = crossLinkStageFromRuntime(xl, stages);
    const source = ORIGIN_TO_SOURCE[inp.origin] || "file_upload";
    const respList =
      rt.responsible_users && rt.responsible_users.length
        ? rt.responsible_users
        : rt.responsible_user
          ? [rt.responsible_user]
          : [];
    const responsible = respList
      .filter((r) => r?.name)
      .map((r) => ({
        id: r.id != null ? r.id : r.email || r.name,
        name: r.name,
        is_current_user: !!r.is_current_user,
      }));
    // ``is_mine`` tem que olhar a lista INTEIRA de responsaveis, nao so
    // o primeiro. Antes, um input com dois responsaveis em que o usuario
    // logado era o segundo aparecia como "nao e meu" nos contadores —
    // subcontava "Meus inputs" vs. a quantidade de cards com minhaDot.
    const isCurrentUserResp = respList.some((r) => r?.is_current_user);
    // ``uploaded_by`` vem do ``InputControlV3.updated_by`` (backend) e
    // e MUTUAMENTE EXCLUSIVO com ``responsible``. Se o backend nao
    // soube quem subiu o arquivo, o campo fica ``null`` — NAO cai no
    // primeiro responsavel.
    const uploadedByRaw = rt.state_params?.uploaded_by || null;
    const uploadedBy = uploadedByRaw
      ? {
          name: uploadedByRaw.name || null,
          email: uploadedByRaw.email || null,
          is_current_user: !!uploadedByRaw.is_current_user,
        }
      : null;
    const textName = inp.file_name || inp.meta_name;
    return {
      input_id: inp.meta_name,
      input_control_id: inp.meta_name,
      meta_name: inp.meta_name,
      input_name: { pt: textName, en: textName, es: textName },
      source,
      // ``expected_file_types`` (plural) e a fonte canonica V3 — lista
      // com 0..N entradas. ``expected_file_type`` (singular) e mantido
      // como derivacao "primeiro item ou null" pra retrocompatibilidade
      // com componentes V2 ainda em uso (ex.: ``InputsV2.js``).
      expected_file_types: TYPES_FROM_INPUT(inp),
      expected_file_type: TYPES_FROM_INPUT(inp)[0] || null,
      // ``volatility`` (monthly/carry_forward/singleton/yearly) —
      // exposto para que a UI possa indicar visualmente inputs
      // "reutilizaveis" (``singleton`` cobre tudo, ``carry_forward``
      // cobre do mes em diante, ``yearly`` cobre o FY corrente).
      // Consumido pelo OverviewV3 (badge) e pelo InputBoxV3 (icone).
      // Classificacao centralizada em
      // ``services/inputsV3Volatility.js``. Rev. 2026-05-28 —
      // ``semi_fixed`` foi removido; o comportamento equivalente
      // agora e' representado por ``monthly`` + flag ``reuse_auto``.
      volatility: inp.volatility || null,
      // ``reuse_allowed`` / ``reuse_auto`` (DB-level, configurados
      // pela UI). Para ``monthly`` com ``reuse_auto=true`` o
      // OverviewV3 e o InputBoxV3 exibem o marker "Repeat"
      // (substitui a antiga derivacao via ``volatility ===
      // 'semi_fixed'``). Para singleton/yearly/carry_forward o
      // marker visual e' driven pela volatility — nao dependem
      // destas flags.
      reuse_allowed: !!rt.reuse_allowed,
      reuse_auto: !!rt.reuse_auto,
      // ``optional``: input cuja aus\u00eancia n\u00e3o bloqueia o
      // calendar de readiness; consumidores devem tratar a falta com
      // fallback (ex.: tax = 0). Consumido pelo InputBoxV3 (\u00edcone)
      // e pelo OverviewV3 (badge).
      optional: !!inp.optional,
      // ``is_waived``: o usu\u00e1rio marcou explicitamente este input
      // opcional como "conclu\u00eddo sem arquivo" via switch do drawer.
      // O backend trata como entregue (``is_permanent_for_month: true``)
      // e os contadores n\u00e3o exibem pend\u00eancia. Para inputs
      // ``yearly``, o waive em qualquer m\u00eas do FY vale para todos
      // os meses do mesmo ano fiscal.
      is_waived: !!(rt.covered_by?.is_waived ?? rt.is_waived),
      waived_at: rt.state_params?.waived_at || null,
      waived_by: rt.state_params?.waived_by || null,
      // ``covered_by`` (PR 2 refactor temporal): janela do control
      // resolvido. Frontend consome em DrawerHeader (badge "Entregue
      // em FEV/2026"), NowPane (periodicity), InputBox (icone
      // "permanente"). ``null`` quando o resolver nao achou control
      // cobrindo o periodo.
      covered_by: rt.covered_by || null,
      stages,
      phase,
      control_state: rt.control_state || null,
      responsible,
      uploaded_by: uploadedBy,
      dependencies: null,
      available_data_from: null,
      notes: [],
      is_required: !isPermanent,
      // ``updated_by_username`` agora reflete quem FEZ o upload (nao o
      // primeiro responsavel). Fica ``null`` para inputs ainda nao
      // entregues ou quando o backend nao registrou o uploader. Os
      // consumidores que queiram fallback para responsavel devem fazer
      // explicitamente no call site.
      updated_by_username: uploadedBy?.name || null,
      // ``updated_in`` e a data (YYYY-MM-DD) em fuso LOCAL. Fazer
      // ``slice(0, 10)`` direto no ISO pega a data UTC — em fusos
      // negativos (Brasil GMT-3) uploads no final do dia rendem a data
      // do dia SEGUINTE, o que confunde o usuario ("enviei dia 17, mas
      // o header mostra 18"). Aqui convertemos para o fuso do browser.
      updated_in: rt.state_params?.delivered_at
        ? formatIsoAsLocalYmd(rt.state_params.delivered_at)
        : null,
      input_details: inp.description || "",
      // ``instructions_md`` (overlay UX, editavel pelo AboutPane).
      // Vem do runtime — nao do snapshot estatico — porque pode
      // mudar a qualquer hora pelo time tecnico.
      instructions_md: rt.instructions_md || null,
      instructions_updated_at: rt.instructions_updated_at || null,
      instructions_updated_by: rt.instructions_updated_by || null,
      // V3-only: flags usadas pelos contadores superiores (nao
      // interferem na UI V2 copiada).
      is_mine: isCurrentUserResp,
      is_permanent_for_month: isPermanent,
      attention_label: rt.attention_label || "pending",
      // ``has_prior_success`` vem do backend (``_build_runtime_for_inputs``)
      // e diz "ja existe pelo menos 1 upload bem-sucedido deste input
      // em algum periodo <= atual". Usado pelo drawer para habilitar/
      // desabilitar "Reusar entrega anterior" — se nao ha entrega
      // previa bem-sucedida, nao ha o que reusar.
      has_prior_success: !!rt.has_prior_success,
      // ``observation_count`` (sev=1 no ultimo job) e exposto no
      // ``state_params`` quando o control esta em ``success``. A UI
      // usa isso para trocar a microcopia do tooltip ("Estrutura
      // conforme o esperado" -> "Sem observacoes criticas") sem
      // precisar buscar ``notes`` em request separado.
      observation_count: Number(rt.state_params?.observation_count || 0),
      // Bloco bruto de cross-linking publicado pelo backend (`runtime.
      // cross_linking`). Consumidores: tooltip do InputBoxV3 (count,
      // state, max_severity) e o donut (worst_fact_coverage).
      cross_linking: xl,
      // ``xlinks_in_flight`` (D-MTX-1): xlinks deste input ainda em
      // pre-execucao (sem CSL ou CSL em pending/blocked/running/stale).
      // Consumidor: InputBoxV3 aplica overlay listrado sobre a cor base
      // do pip enquanto >0 — pip mantem o veredito agregado e ganha
      // sinal visual de "tem coisa rodando agora".
      xlinks_in_flight: Number(xl?.xlinks_in_flight || 0),
      // ``quality_score`` (0..100): % de cobertura do PIOR cross-link
      // em que o input e ``fact``. Calculado como ``100 * (1 - missing
      // ratio)`` quando ha breach quantificado. ``null`` quando:
      //   (a) input nao participa de cross-link como fact, OU
      //   (b) todos os cross-links de fato passaram limpos (ainda nao
      //       persistimos a coverage de OK; UI mostra "—" e o donut
      //       fica cheio na cor do estado).
      quality_score:
        xl?.worst_fact_coverage?.ratio != null
          ? Math.max(
              0,
              Math.min(
                100,
                Math.round(100 * (1 - xl.worst_fact_coverage.ratio)),
              ),
            )
          : null,
    };
  };

  const out = [];
  for (const g of groupsSorted) {
    const list = byGroup.get(g.meta_name) || [];
    if (list.length === 0) continue;
    out.push({
      group_meta_name: g.meta_name,
      group_name: {
        pt: g.display_name,
        en: g.display_name,
        es: g.display_name,
      },
      inputs: list.map(makeInputRow),
    });
  }
  if (ungrouped.length > 0) {
    out.push({
      group_meta_name: "__ungrouped__",
      group_name: {
        pt: "Sem agrupamento",
        en: "Ungrouped",
        es: "Sin agrupamiento",
      },
      inputs: ungrouped.map(makeInputRow),
    });
  }
  return out;
}

/**
 * Shape do endpoint V2 ``inputs_status_all_periods``, construído do
 * mesmo snapshot — no snapshot-first temos um mês só, então a tabela
 * histórica degenera mas preserva a estrutura da view.
 */
function buildGeneralStatus(snapshot, runtime, period) {
  const ym = period.year * 100 + period.month;
  const byMonth = {};
  byMonth[ym] = [];
  const groupsDescriptions = {};
  const inputsDescriptions = {};
  const inputsResponsible = {};

  const groupsSorted = [...(snapshot.input_groups || [])].sort(
    (a, b) => (a.position || 0) - (b.position || 0),
  );
  for (const g of groupsSorted) {
    groupsDescriptions[g.meta_name] = {
      pt: g.display_name,
      en: g.display_name,
      es: g.display_name,
    };
  }

  const byGroup = new Map();
  for (const g of groupsSorted) byGroup.set(g.meta_name, []);
  const ungrouped = [];
  for (const inp of snapshot.inputs || []) {
    const rt = runtime.inputs?.[inp.meta_name] || {};
    const isPermanent = !!rt.is_permanent_for_month;
    const phase = derivePhase({
      attentionLabel: rt.attention_label,
      controlState: rt.control_state || null,
      isPermanent,
    });
    const stages = phaseToStages(phase);
    inputsDescriptions[inp.meta_name] = {
      input_name: {
        pt: inp.file_name || inp.meta_name,
        en: inp.file_name || inp.meta_name,
        es: inp.file_name || inp.meta_name,
      },
    };
    if (rt.responsible_user?.name) {
      inputsResponsible[inp.meta_name] = [rt.responsible_user.name];
    }
    const row = {
      input_id: inp.meta_name,
      data_source: stages.data_source,
      data_structure: stages.data_structure,
      cross_linking: stages.cross_linking,
    };
    const groupKey = inp.group_meta_name;
    if (groupKey && byGroup.has(groupKey)) byGroup.get(groupKey).push(row);
    else ungrouped.push(row);
  }

  for (const g of groupsSorted) {
    const inputs = byGroup.get(g.meta_name) || [];
    if (inputs.length === 0) continue;
    byMonth[ym].push({ group_meta_name: g.meta_name, inputs });
  }
  if (ungrouped.length > 0) {
    byMonth[ym].push({ group_meta_name: "__ungrouped__", inputs: ungrouped });
    groupsDescriptions["__ungrouped__"] = {
      pt: "Sem agrupamento",
      en: "Ungrouped",
      es: "Sin agrupamiento",
    };
  }

  return {
    inputs: byMonth,
    groups_descriptions: groupsDescriptions,
    inputs_descriptions: inputsDescriptions,
    inputs_responsible: inputsResponsible,
  };
}

/**
 * Monta a lista de meses exibida pelo MonthSelectorV3.
 *
 * Regra:
 *  - Se ``implementation.first_year``/``first_month`` existem, gera
 *    todos os ``(year, month)`` de first_year/first_month ate o ``period``
 *    (ou ate hoje se ``is_active``), inclusive.
 *  - O mes que casa com ``period`` recebe ``focusStatus`` +
 *    ``focusTotalInputs``; os demais ficam ``waiting`` (cinza neutro no
 *    seletor) ate o usuario clicar neles e o backend devolver runtime.
 *  - Se ``implementation`` nao traz a janela, cai no comportamento
 *    legacy (array de 1 elemento = apenas o periodo).
 */
function buildMonthlyStatusWindow({
  implementation,
  period,
  focusStatus,
  focusTotalInputs,
}) {
  const firstYear = implementation?.first_year;
  const firstMonth = implementation?.first_month;
  const legacy = [
    {
      year: period.year,
      month: period.month,
      status: focusStatus,
      total_inputs: focusTotalInputs,
      pending_jobs_count: 0,
    },
  ];
  if (!firstYear || !firstMonth) return legacy;

  // Regra de negocio: **so se calculam meses fechados**. O mes
  // corrente ainda nao fechou, entao nao entra no seletor — o ultimo
  // mes "aberto" (= ultimo fechado) e `mes_atual - 1`. Se a
  // implementacao nao e ativa, respeitamos o ``period`` que o backend
  // ja devolveu (presumidamente um mes fechado).
  const isActive = implementation.is_active !== false;
  const now = new Date();
  const curIdx = now.getUTCFullYear() * 12 + now.getUTCMonth(); // mes atual (0-based)
  const lastClosedIdx = curIdx - 1; // ultimo mes fechado
  const lastIdx = isActive
    ? lastClosedIdx
    : period.year * 12 + (period.month - 1);

  const startIdx = firstYear * 12 + (firstMonth - 1);
  const endIdx = lastIdx;
  if (endIdx < startIdx) return legacy;

  const out = [];
  for (let idx = startIdx; idx <= endIdx; idx += 1) {
    const y = Math.floor(idx / 12);
    const m = (idx % 12) + 1;
    const isFocus = y === period.year && m === period.month;
    out.push({
      year: y,
      month: m,
      status: isFocus ? focusStatus : "waiting",
      total_inputs: isFocus ? focusTotalInputs : 0,
      pending_jobs_count: 0,
    });
  }
  return out;
}

/**
 * Entry point — transforma o payload V3 no shape V2 consumido pela
 * view. Retorna todas as estruturas ``setX`` do InputsV3.js em uma só
 * chamada, pois o snapshot já traz tudo em uma request.
 */
export function adaptSnapshotToV2Shape(payload) {
  const { snapshot, runtime, period } = payload;

  const inputsStatus = buildInputsStatus(snapshot, runtime);
  // Prioriza ``payload.month_status`` (canonico, computado pelo backend
  // com a regra completa do ``progress_overview``: inputs + blocos +
  // cross-links + jobs ativos). Fallback ``deriveMonthStatus`` cobre
  // backends antigos que ainda nao expoem o campo.
  const canonicalMonthStatus =
    typeof payload.month_status === "string" ? payload.month_status : null;
  // D-MTX-2: ``month_status_reason`` qualifica buckets que admitem
  // mais de um motivo (``processing/blocks`` vs ``processing/crosslinks``,
  // ``waiting/inputs`` vs ``waiting/no_inputs``). Frontend usa esse
  // campo para escolher o copy contextual sem inflar a paleta.
  const canonicalMonthStatusReason =
    typeof payload.month_status_reason === "string"
      ? payload.month_status_reason
      : null;
  // Onda 1.8c: ``month_status_active`` lista os atores rodando agora
  // (parses, xlinks, blocos) — alimenta o texto contextual ao lado do
  // badge ("Lendo Venta Bruta...", "Cruzando Venta x Ajustes...",
  // "Processando PnL Logistica..."). Backend pode nao enviar (versoes
  // antigas) — defaultamos para ``null`` e o frontend cai no copy
  // generico do badge.
  const canonicalMonthStatusActive =
    payload.month_status_active &&
    typeof payload.month_status_active === "object"
      ? payload.month_status_active
      : null;
  const monthStatus = canonicalMonthStatus || deriveMonthStatus(inputsStatus);

  const totalInputs = inputsStatus.reduce(
    (acc, g) => acc + (g.inputs?.length || 0),
    0,
  );

  // Expande a janela operacional (leva 57-F fix). Backend agora envia
  // ``first_year``/``first_month``/``is_active`` dentro de
  // ``implementation``. Monta-se a lista de meses da janela e o mes
  // "foco" (``period``) recebe o status calculado; os demais ficam
  // como ``waiting`` — a UI re-faz snapshot com ``?year&month`` quando
  // o usuario troca de mes, enriquecendo sob demanda. Fallback antigo
  // (so o ``period``) e preservado para backends que ainda nao subiram.
  const monthlyStatus = buildMonthlyStatusWindow({
    implementation: payload.implementation,
    period,
    focusStatus: monthStatus,
    focusTotalInputs: totalInputs,
  });

  const users = collectUsers(runtime);
  const generalStatus = buildGeneralStatus(snapshot, runtime, period);

  return {
    monthlyStatus,
    // Status canonico do mes selecionado (vem do backend, regra completa).
    // Usado pelo ``InputsV3.js`` para sobrepor o status do mes ativo
    // tanto no badge quanto no seletor de meses, sem depender do polling
    // do ``progress_overview``.
    selectedMonthStatus: canonicalMonthStatus,
    selectedMonthStatusReason: canonicalMonthStatusReason,
    // Onda 1.8c: payload de atores rodando agora (parses, crosslinks,
    // blocks) usado pelo ``ModelingProgressBandV3`` para narrar
    // contextualmente o que esta sendo processado.
    selectedMonthStatusActive: canonicalMonthStatusActive,
    inputsStatus,
    users,
    generalStatus,
    calculationLog: null,
    // Expõe o bloco ``implementation`` cru para callers que precisam
    // chamar endpoints V3 por path (``/api/v3/inputs/<app_name>/...``),
    // como o upload da Fase D. Manter aqui (e não só no
    // inputsV3Service) evita que cada consumidor precise guardar um
    // state paralelo pra uma informação que já vem no snapshot.
    implementation: payload.implementation || null,
  };
}
